import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Settings2, Plus, Trash2, Pencil, Palette, ListTree, Loader2, Share2, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTITY_TYPES = [
  { value: "client",          label: "Clients" },
  { value: "company",         label: "Companies" },
  { value: "quote",           label: "Quotes" },
  { value: "schedule",        label: "Jobs / Schedules" },
  { value: "invoice",         label: "Invoices" },
  { value: "inventory_item",  label: "Inventory" },
  { value: "employee",        label: "Employees" },
  { value: "expense",         label: "Expenses" },
];

const FIELD_TYPES = [
  { value: "text",          label: "Text (single line)" },
  { value: "long_text",     label: "Text area (paragraph)" },
  { value: "number",        label: "Number" },
  { value: "currency",      label: "Currency" },
  { value: "date",          label: "Date" },
  { value: "select",        label: "Drop-down (single select)" },
  { value: "multi_select",  label: "Multi-select" },
  { value: "checkbox",      label: "Checkbox (yes/no)" },
  { value: "email",         label: "Email" },
  { value: "phone",         label: "Phone" },
  { value: "url",           label: "URL" },
];

const blankFieldDef = {
  entity_type: "client",
  field_key: "",
  label: "",
  field_type: "text",
  options: [],
  placeholder: "",
  help_text: "",
  is_required: false,
  is_active: true,
  sort_order: 0,
  show_to_admin: true,
  show_to_employee: false,
  show_to_client: false,
  employee_can_edit: false,
  client_can_edit: false,
};

const blankStatus = {
  name: "",
  color: "#64748b",
  sort_order: 0,
  is_completed_state: false,
  is_cancelled_state: false,
  is_default: false,
  description: "",
};

export default function CustomizationCenter() {
  const { user } = useAuth();
  const isAdmin = user?.role === "office_manager" || user?.role === "field_supervisor";

  // ===== Custom Fields =====
  const [entityFilter, setEntityFilter] = useState("client");
  const [defs, setDefs] = useState([]);
  const [defOpen, setDefOpen] = useState(false);
  const [editingDef, setEditingDef] = useState(null);
  const [defLoading, setDefLoading] = useState(false);

  // ===== Job Statuses =====
  const [statuses, setStatuses] = useState([]);
  const [statusOpen, setStatusOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  // iter147 — Contribute-as-template dialog state
  const [contribOpen, setContribOpen] = useState(false);
  const [contribForm, setContribForm] = useState({
    name: "", description: "", vertical_tags: "", concept_tags: "",
  });
  const [contribBusy, setContribBusy] = useState(false);

  const submitContribution = async () => {
    if (!contribForm.name.trim()) {
      toast.error("Give your template a name so other businesses can find it.");
      return;
    }
    setContribBusy(true);
    try {
      const res = await axios.post(`${API}/platform/templates/contribute`, contribForm);
      toast.success(res.data?.message || "Template submitted for review");
      setContribOpen(false);
      setContribForm({ name: "", description: "", vertical_tags: "", concept_tags: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setContribBusy(false); }
  };

  useEffect(() => { fetchDefs(); }, [entityFilter]);
  useEffect(() => { fetchStatuses(); }, []);

  const fetchDefs = async () => {
    setDefLoading(true);
    try {
      const r = await axios.get(`${API}/custom-fields/defs?entity_type=${entityFilter}&include_inactive=true`);
      setDefs(r.data || []);
    } catch {
      toast.error("Failed to load custom fields");
    } finally { setDefLoading(false); }
  };

  const fetchStatuses = async () => {
    setStatusLoading(true);
    try {
      const r = await axios.get(`${API}/job-statuses?include_inactive=true`);
      setStatuses(r.data || []);
    } catch {
      toast.error("Failed to load job statuses");
    } finally { setStatusLoading(false); }
  };

  const saveDef = async () => {
    if (!editingDef?.label || !editingDef?.field_key) { toast.error("Label and key required"); return; }
    try {
      if (editingDef.id) {
        await axios.put(`${API}/custom-fields/defs/${editingDef.id}`, editingDef);
        toast.success("Field updated");
      } else {
        await axios.post(`${API}/custom-fields/defs`, editingDef);
        toast.success("Field added");
      }
      setDefOpen(false);
      setEditingDef(null);
      fetchDefs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteDef = async (id) => {
    if (!window.confirm("Delete this custom field? Existing values will be removed.")) return;
    try {
      await axios.delete(`${API}/custom-fields/defs/${id}`);
      toast.success("Field deleted");
      fetchDefs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const saveStatus = async () => {
    if (!editingStatus?.name) { toast.error("Name required"); return; }
    try {
      if (editingStatus.id) {
        await axios.put(`${API}/job-statuses/${editingStatus.id}`, editingStatus);
        toast.success("Status updated");
      } else {
        await axios.post(`${API}/job-statuses`, editingStatus);
        toast.success("Status added");
      }
      setStatusOpen(false);
      setEditingStatus(null);
      fetchStatuses();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteStatus = async (id) => {
    if (!window.confirm("Deactivate this status? Existing jobs already on it will keep the status text.")) return;
    try {
      await axios.delete(`${API}/job-statuses/${id}`);
      toast.success("Status deactivated");
      fetchStatuses();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Only admins can manage customization.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="customization-center">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-primary/10"><Settings2 className="h-7 w-7 text-primary" /></div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Customize Business</h1>
            <p className="text-muted-foreground">Adapt Business Genie to fit your business — custom fields, custom job statuses, and (coming next) sub-client hierarchy.</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setContribOpen(true)}
          className="gap-2 border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10"
          data-testid="contribute-template-btn"
        >
          <Share2 className="h-4 w-4" /> Contribute as Template
        </Button>
      </div>

      {/* Contribute-as-template dialog */}
      <Dialog open={contribOpen} onOpenChange={setContribOpen}>
        <DialogContent className="max-w-lg" data-testid="contribute-template-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-600" /> Share your setup with other businesses
            </DialogTitle>
            <DialogDescription>
              Submit your current job statuses + custom fields as a starter template.
              After a quick review, it&apos;ll appear in the guided tour for other businesses
              of your type — helping them get up and running in seconds.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Template name *</Label>
              <Input
                value={contribForm.name}
                onChange={(e) => setContribForm({ ...contribForm, name: e.target.value })}
                placeholder="e.g. Pet Grooming Salon, Food Truck, Notary Services"
                data-testid="contrib-name"
              />
            </div>
            <div>
              <Label>Short description</Label>
              <Textarea
                value={contribForm.description}
                onChange={(e) => setContribForm({ ...contribForm, description: e.target.value })}
                placeholder="A one-liner about what this template is best for."
                rows={2}
                data-testid="contrib-description"
              />
            </div>
            <div>
              <Label>Vertical tags (comma-separated)</Label>
              <Input
                value={contribForm.vertical_tags}
                onChange={(e) => setContribForm({ ...contribForm, vertical_tags: e.target.value })}
                placeholder="pet-grooming, mobile, salon"
                data-testid="contrib-vertical-tags"
              />
              <p className="text-xs text-muted-foreground mt-1">Helps the guided tour match your template to similar businesses.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setContribOpen(false)} disabled={contribBusy}>Cancel</Button>
            <Button onClick={submitContribution} disabled={contribBusy} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="contrib-submit">
              {contribBusy && <Loader2 className="h-4 w-4 animate-spin" />}
              <Share2 className="h-4 w-4" /> Submit for Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="fields">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="fields" className="flex items-center gap-2" data-testid="tab-custom-fields"><Palette className="h-4 w-4" /> Custom Fields</TabsTrigger>
          <TabsTrigger value="statuses" className="flex items-center gap-2" data-testid="tab-job-statuses"><ListTree className="h-4 w-4" /> Job Statuses</TabsTrigger>
        </TabsList>

        {/* ===== Custom Fields ===== */}
        <TabsContent value="fields" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <CardTitle>Custom Fields</CardTitle>
                  <CardDescription>Add extra fields to any entity. Choose who sees and edits each one (admin, employees, clients).</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={entityFilter} onValueChange={setEntityFilter}>
                    <SelectTrigger className="w-48" data-testid="entity-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ENTITY_TYPES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={() => { setEditingDef({ ...blankFieldDef, entity_type: entityFilter }); setDefOpen(true); }} data-testid="add-field-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add field
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {defLoading ? (
                <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : defs.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Palette className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>No custom fields for {ENTITY_TYPES.find(e => e.value === entityFilter)?.label} yet.</p>
                  <p className="text-xs mt-1">Click <strong>Add field</strong> above to create your first one.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Label</TableHead>
                      <TableHead>Key</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Visible to</TableHead>
                      <TableHead>Editable by</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {defs.map((d) => (
                      <TableRow key={d.id} className={!d.is_active ? "opacity-50" : ""}>
                        <TableCell className="font-medium">
                          {d.label}
                          {d.is_required && <Badge variant="outline" className="ml-2 text-xs">Required</Badge>}
                          {!d.is_active && <Badge variant="outline" className="ml-2 text-xs">Inactive</Badge>}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{d.field_key}</TableCell>
                        <TableCell><Badge variant="secondary">{FIELD_TYPES.find(t => t.value === d.field_type)?.label || d.field_type}</Badge></TableCell>
                        <TableCell className="text-xs space-y-0.5">
                          {d.show_to_admin && <Badge variant="outline" className="mr-1">Admin</Badge>}
                          {d.show_to_employee && <Badge variant="outline" className="mr-1">Staff</Badge>}
                          {d.show_to_client && <Badge variant="outline">Client</Badge>}
                        </TableCell>
                        <TableCell className="text-xs">
                          {d.employee_can_edit && <Badge variant="outline" className="mr-1">Staff</Badge>}
                          {d.client_can_edit && <Badge variant="outline">Client</Badge>}
                          {!d.employee_can_edit && !d.client_can_edit && <span className="text-muted-foreground">Admin only</span>}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => { setEditingDef(d); setDefOpen(true); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteDef(d.id)} className="text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Job Statuses ===== */}
        <TabsContent value="statuses" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <CardTitle>Job Statuses</CardTitle>
                  <CardDescription>Define your own Kanban states. Default statuses are auto-seeded on first use — customize, add, reorder or deactivate.</CardDescription>
                </div>
                <Button onClick={() => { setEditingStatus({ ...blankStatus }); setStatusOpen(true); }} data-testid="add-status-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add status
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {statusLoading ? (
                <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Order</TableHead>
                      <TableHead>Flags</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {statuses.map((s) => (
                      <TableRow key={s.id} className={!s.is_active ? "opacity-50" : ""}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="inline-block w-4 h-4 rounded-full border" style={{ background: s.color }} />
                            <span className="font-medium">{s.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>{s.sort_order}</TableCell>
                        <TableCell className="text-xs space-x-1">
                          {s.is_default && <Badge variant="default">Default</Badge>}
                          {s.is_completed_state && <Badge variant="outline" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Completed-state</Badge>}
                          {s.is_cancelled_state && <Badge variant="outline" className="bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">Cancelled-state</Badge>}
                          {!s.is_active && <Badge variant="outline">Inactive</Badge>}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => { setEditingStatus(s); setStatusOpen(true); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteStatus(s.id)} className="text-destructive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ===== Field editor dialog ===== */}
      <Dialog open={defOpen} onOpenChange={(o) => { setDefOpen(o); if (!o) setEditingDef(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingDef?.id ? "Edit custom field" : "New custom field"}</DialogTitle>
            <DialogDescription>Fields show up on the matching entity&apos;s edit form, scoped to the audiences you toggle below.</DialogDescription>
          </DialogHeader>
          {editingDef && (
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Applies to</Label>
                  <Select value={editingDef.entity_type} onValueChange={(v) => setEditingDef({ ...editingDef, entity_type: v })} disabled={!!editingDef.id}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ENTITY_TYPES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Field type</Label>
                  <Select value={editingDef.field_type} onValueChange={(v) => setEditingDef({ ...editingDef, field_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Label (what users see)</Label>
                  <Input value={editingDef.label} onChange={(e) => setEditingDef({ ...editingDef, label: e.target.value, field_key: editingDef.id ? editingDef.field_key : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_") })} data-testid="field-label-input" />
                </div>
                <div>
                  <Label>Key (machine-name, no spaces)</Label>
                  <Input value={editingDef.field_key} onChange={(e) => setEditingDef({ ...editingDef, field_key: e.target.value })} className="font-mono text-xs" disabled={!!editingDef.id} data-testid="field-key-input" />
                </div>
              </div>
              {(editingDef.field_type === "select" || editingDef.field_type === "multi_select") && (
                <div>
                  <Label>Options (one per line)</Label>
                  <Textarea
                    rows={4}
                    value={(editingDef.options || []).join("\n")}
                    onChange={(e) => setEditingDef({ ...editingDef, options: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })}
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Help text</Label>
                  <Input value={editingDef.help_text || ""} onChange={(e) => setEditingDef({ ...editingDef, help_text: e.target.value })} />
                </div>
                <div>
                  <Label>Placeholder</Label>
                  <Input value={editingDef.placeholder || ""} onChange={(e) => setEditingDef({ ...editingDef, placeholder: e.target.value })} />
                </div>
              </div>

              <div className="rounded-lg border p-3 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Visibility</p>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={editingDef.show_to_admin} onCheckedChange={(v) => setEditingDef({ ...editingDef, show_to_admin: !!v })} /> Admin</label>
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={editingDef.show_to_employee} onCheckedChange={(v) => setEditingDef({ ...editingDef, show_to_employee: !!v })} /> Employee portal</label>
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={editingDef.show_to_client} onCheckedChange={(v) => setEditingDef({ ...editingDef, show_to_client: !!v })} /> Client portal</label>
                </div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2">Edit permission</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={editingDef.employee_can_edit} onCheckedChange={(v) => setEditingDef({ ...editingDef, employee_can_edit: !!v })} /> Employees can edit</label>
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={editingDef.client_can_edit} onCheckedChange={(v) => setEditingDef({ ...editingDef, client_can_edit: !!v })} /> Clients can edit</label>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer text-sm"><Checkbox checked={editingDef.is_required} onCheckedChange={(v) => setEditingDef({ ...editingDef, is_required: !!v })} /> Required</label>
                <label className="flex items-center gap-2 cursor-pointer text-sm"><Checkbox checked={editingDef.is_active} onCheckedChange={(v) => setEditingDef({ ...editingDef, is_active: !!v })} /> Active</label>
                <div className="ml-auto flex items-center gap-2 text-sm">
                  <Label className="m-0">Sort</Label>
                  <Input type="number" className="w-20" value={editingDef.sort_order || 0} onChange={(e) => setEditingDef({ ...editingDef, sort_order: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDefOpen(false)}>Cancel</Button>
            <Button onClick={saveDef} data-testid="save-field-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Status editor dialog ===== */}
      <Dialog open={statusOpen} onOpenChange={(o) => { setStatusOpen(o); if (!o) setEditingStatus(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingStatus?.id ? "Edit status" : "New status"}</DialogTitle>
            <DialogDescription>Use this to model your workflow — e.g., Diagnosed, Awaiting Parts, On Hold.</DialogDescription>
          </DialogHeader>
          {editingStatus && (
            <div className="grid gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Name</Label>
                  <Input value={editingStatus.name} onChange={(e) => setEditingStatus({ ...editingStatus, name: e.target.value })} data-testid="status-name-input" />
                </div>
                <div>
                  <Label>Color</Label>
                  <div className="flex items-center gap-2">
                    <Input type="color" value={editingStatus.color} onChange={(e) => setEditingStatus({ ...editingStatus, color: e.target.value })} className="h-10 w-16 p-1" />
                    <Input value={editingStatus.color} onChange={(e) => setEditingStatus({ ...editingStatus, color: e.target.value })} className="font-mono text-xs" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Sort order</Label>
                  <Input type="number" value={editingStatus.sort_order} onChange={(e) => setEditingStatus({ ...editingStatus, sort_order: parseInt(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input value={editingStatus.description || ""} onChange={(e) => setEditingStatus({ ...editingStatus, description: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2 rounded-lg border p-3">
                <label className="flex items-center gap-2 cursor-pointer text-sm"><Checkbox checked={editingStatus.is_default} onCheckedChange={(v) => setEditingStatus({ ...editingStatus, is_default: !!v })} /> Default status for new jobs</label>
                <label className="flex items-center gap-2 cursor-pointer text-sm"><Checkbox checked={editingStatus.is_completed_state} onCheckedChange={(v) => setEditingStatus({ ...editingStatus, is_completed_state: !!v })} /> Counts as &quot;completed&quot; for reporting</label>
                <label className="flex items-center gap-2 cursor-pointer text-sm"><Checkbox checked={editingStatus.is_cancelled_state} onCheckedChange={(v) => setEditingStatus({ ...editingStatus, is_cancelled_state: !!v })} /> Counts as &quot;cancelled&quot;</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)}>Cancel</Button>
            <Button onClick={saveStatus} data-testid="save-status-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
